import React, { useState } from 'react';
import { Badge } from '../ui/Badge';
import { ArrowUpDown, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
const PAGE_SIZE = 10;
export function IssueTableArtifact({
  data
}: {
  data: any[];
}) {
  const [sortField, setSortField] = useState<string>('key');
  const [sortDesc, setSortDesc] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  if (!Array.isArray(data) || data.length === 0) {
    return <div className="p-4 text-slate-500 text-sm" data-id="element-238">
        No hay issues para mostrar.
      </div>;
  }
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDesc(!sortDesc);
    } else {
      setSortField(field);
      setSortDesc(false);
    }
    setCurrentPage(0); // Reset to first page on sort change
  };
  const sortedData = [...data].sort((a, b) => {
    let valA = a[sortField] || a.fields?.[sortField] || '';
    let valB = b[sortField] || b.fields?.[sortField] || '';
    if (sortField === 'status') {
      valA = a.fields?.status?.name || a.status || '';
      valB = b.fields?.status?.name || b.status || '';
    } else if (sortField === 'priority') {
      valA = a.fields?.priority?.name || a.priority || '';
      valB = b.fields?.priority?.name || b.priority || '';
    } else if (sortField === 'assignee') {
      valA = a.fields?.assignee?.displayName || a.assignee?.name || '';
      valB = b.fields?.assignee?.displayName || b.assignee?.name || '';
    }
    if (valA < valB) return sortDesc ? 1 : -1;
    if (valA > valB) return sortDesc ? -1 : 1;
    return 0;
  });
  // Pagination
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIdx = currentPage * PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAGE_SIZE, totalItems);
  const pageData = sortedData.slice(startIdx, endIdx);
  const showPagination = totalItems > PAGE_SIZE;
  const getStatusColor = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('done') || s.includes('closed') || s.includes('resolved')) return 'bg-emerald-900/50 text-emerald-400 border-emerald-800';
    if (s.includes('progress') || s.includes('review')) return 'bg-blue-900/50 text-blue-400 border-blue-800';
    return 'bg-slate-800 text-slate-300 border-slate-700';
  };
  const getPriorityColor = (priority: string) => {
    const p = priority?.toLowerCase() || '';
    if (p.includes('high') || p.includes('highest') || p.includes('blocker')) return 'text-red-400';
    if (p.includes('medium')) return 'text-amber-400';
    return 'text-emerald-400';
  };
  return <div data-id="element-239">
      <div className="overflow-x-auto" data-id="element-240">
        <table className="w-full text-sm text-left" data-id="element-241">
          <thead className="text-xs text-slate-400 uppercase bg-slate-800/50 border-b border-slate-700" data-id="element-242">
            <tr data-id="element-243">
              <th className="px-4 py-3 font-medium cursor-pointer hover:text-slate-200" onClick={() => handleSort('key')} data-id="element-244">
                <div className="flex items-center gap-1" data-id="element-245">
                  Key <ArrowUpDown size={12} data-id="element-246" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium cursor-pointer hover:text-slate-200" onClick={() => handleSort('summary')} data-id="element-247">
                <div className="flex items-center gap-1" data-id="element-248">
                  Summary <ArrowUpDown size={12} data-id="element-249" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium cursor-pointer hover:text-slate-200" onClick={() => handleSort('status')} data-id="element-250">
                <div className="flex items-center gap-1" data-id="element-251">
                  Status <ArrowUpDown size={12} data-id="element-252" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium cursor-pointer hover:text-slate-200" onClick={() => handleSort('priority')} data-id="element-253">
                <div className="flex items-center gap-1" data-id="element-254">
                  Priority <ArrowUpDown size={12} data-id="element-255" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium cursor-pointer hover:text-slate-200" onClick={() => handleSort('assignee')} data-id="element-256">
                <div className="flex items-center gap-1" data-id="element-257">
                  Assignee <ArrowUpDown size={12} data-id="element-258" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50" data-id="element-259">
            {pageData.map((issue, idx) => {
            const key = issue.key;
            const summary = issue.fields?.summary || issue.summary || 'Sin título';
            const status = issue.fields?.status?.name || issue.status || 'To Do';
            const priority = issue.fields?.priority?.name || issue.priority || 'Medium';
            const assignee = issue.fields?.assignee?.displayName || issue.assignee?.name || 'Unassigned';
            return <tr key={key || idx} className="hover:bg-slate-800/30 transition-colors" data-id="element-260">
                  <td className="px-4 py-2.5 font-medium text-indigo-400 whitespace-nowrap" data-id="element-261">
                    <a href="#" className="hover:underline flex items-center gap-1" data-id="element-262">
                      {key} <ExternalLink size={10} data-id="element-263" />
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-slate-200 max-w-xs truncate" title={summary} data-id="element-264">
                    {summary}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap" data-id="element-265">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${getStatusColor(status)}`} data-id="element-266">
                      {status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap" data-id="element-267">
                    <span className={`text-xs font-medium ${getPriorityColor(priority)}`} data-id="element-268">
                      {priority}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap text-xs" data-id="element-269">
                    {assignee}
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {showPagination && <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-700/50 bg-slate-800/30" data-id="element-270">
          <span className="text-xs text-slate-500" data-id="element-271">
            {startIdx + 1}–{endIdx} de {totalItems}
          </span>
          <div className="flex items-center gap-1" data-id="element-272">
            <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} className="p-1 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Anterior" data-id="element-273">
              <ChevronLeft size={16} data-id="element-274" />
            </button>
            {Array.from({
          length: totalPages
        }, (_, i) => <button key={i} onClick={() => setCurrentPage(i)} className={`w-6 h-6 rounded text-xs font-medium transition-colors ${i === currentPage ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'}`} data-id="element-275">
                  {i + 1}
                </button>)}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage === totalPages - 1} className="p-1 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Siguiente" data-id="element-276">
              <ChevronRight size={16} data-id="element-277" />
            </button>
          </div>
        </div>}
    </div>;
}